/**
 * TenantKartotekaListBuilderModal — modal projektowania WIDOKU LISTY kartoteki
 * z akordeonu pozycji menu w kreatorze menu TENANTA (2026-07-03, feedback Eugenii:
 * „Przeglądaj/edytuj" ma być przy KAŻDYM elemencie, nie tylko formularzach).
 *
 * Osadza `SystemModuleListBuilderPage` w NATYWNYCH scope'ach tenanta:
 *  - kartoteka z Kreatora/własna → scope='module' (builder rozwiązuje slug→id,
 *    persystencja /modules/{id}/layouts przez useTableLayout),
 *  - kartoteka systemowa → scope='system' (/system-modules/{slug}/layouts).
 * `forcedLayoutId` (sufiks tableId) celuje panele w KONKRETNY layout z wiersza
 * akordeonu — zapis w panelach jest natychmiastowy (natywny PUT buildera),
 * więc modal nie ma własnego przycisku Zapisz ani dirty-guarda; rodzic po
 * zamknięciu invaliduje źródła kartoteki (odświeżenie wierszy/chipów/podglądu).
 */

import { useTranslation } from 'react-i18next';

import { SystemModuleListBuilderPage } from '@/pages/system-modules/SystemModuleListBuilderPage';

import { BuilderOverlay } from './BuilderOverlay';

interface TenantKartotekaListBuilderModalProps {
  scope: 'module' | 'system';
  /** Slug kartoteki (scope 'module': builder sam rozwiązuje na ID modułu). */
  code: string;
  /** Layout z klikniętego wiersza (null = domyślny bez zapisanego layoutu). */
  forcedLayoutId: string | null;
  entityName: string;
  /** Nazwa layoutu (null = domyślny). */
  layoutName: string | null;
  onClose: () => void;
  /** Podgląd domyślnego — builder read-only. */
  readOnly?: boolean;
}

export function TenantKartotekaListBuilderModal({
  scope,
  code,
  forcedLayoutId,
  entityName,
  layoutName,
  onClose,
  readOnly = false,
}: TenantKartotekaListBuilderModalProps) {
  const { t } = useTranslation();
  return (
    <BuilderOverlay
      testId="tenant-kartoteka-list-builder-modal"
      onRequestClose={onClose}
      width="wide"
      title={t('admin.templateMenuBuilder.viewBuilder.title', {
        defaultValue: 'Projektowanie widoku listy',
      })}
      subtitle={`${entityName}${layoutName ? ` · ${layoutName}` : ''}`}
      banner={
        readOnly ? (
          <span
            className="rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            data-testid="tenant-list-builder-read-only-banner"
          >
            {t('admin.menuItemViews.readOnlyBanner', {
              defaultValue: 'Podgląd — tylko do odczytu, nie można edytować',
            })}
          </span>
        ) : undefined
      }
      closeLabel={t('common.close', { defaultValue: 'Zamknij' })}
      closeTestId="tenant-kartoteka-list-builder-close"
    >
      <SystemModuleListBuilderPage
        scope={scope}
        embeddedMode
        code={code}
        readOnlyMode={readOnly}
        forcedLayoutId={forcedLayoutId ?? undefined}
      />
    </BuilderOverlay>
  );
}
